// Thin client hook for Organisation company-context grounding (spec 022 FR-004,
// SC-002).
//
// The UI reaches the new backend route through this hook, never `fetch` inside a
// component (constitution III). It POSTs the organisation to
// /api/organisation/ground and resolves to the MANDATORY degradation contract:
//
//   { ok: true,  background }              on 2xx with a valid body
//   { ok: false, reason: 'unavailable' }   on non-2xx / 429 / network reject
//   { ok: false }                          on a malformed / absent body
//
// It NEVER throws and NEVER fabricates a draft — on any failure the surface
// degrades to the manual background floor. Mirrors useVisionFraming.

import { useCallback } from 'react';

export interface OrgGroundingOrg {
  name: string;
  descriptor?: string;
  website?: string;
}

export interface GroundedBackground {
  text: string;
  attribution: string;
  flaggedClaims: string[];
  sources?: { title: string; url: string }[];
}

export type GroundResult =
  | { ok: true; background: GroundedBackground }
  | { ok: false; reason?: 'unavailable' };

export interface UseOrganisationGroundingReturn {
  ground: (org: OrgGroundingOrg) => Promise<GroundResult>;
}

const UNAVAILABLE: GroundResult = { ok: false, reason: 'unavailable' };
const MALFORMED: GroundResult = { ok: false };

export function useOrganisationGrounding(): UseOrganisationGroundingReturn {
  const ground = useCallback(async (org: OrgGroundingOrg): Promise<GroundResult> => {
    try {
      const res = await fetch('/api/organisation/ground', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: org?.name,
          descriptor: org?.descriptor,
          website: org?.website,
        }),
      });
      if (!res.ok) return UNAVAILABLE;
      const data = await res.json();
      if (!data || typeof data !== 'object' || !data.background) return MALFORMED;
      return {
        ok: true,
        background: data.background as GroundedBackground,
      };
    } catch {
      return UNAVAILABLE;
    }
  }, []);

  return { ground };
}
